/**
 * Sandboxed `{{ }}` expression interpreter for the page runtime
 * (ADR-0010 D12/D13 · P7b1).
 *
 * The browser twin of the server-side QuickJS layer
 * (`hub.workflow.js_expr_layer`). Portal end-users execute pages, so this
 * MUST be a restricted interpreter over a parsed AST — never `eval` /
 * `new Function`. It evaluates a **safe JS subset** against a caller-built
 * `scope` and can reach nothing else: no `window`, no `globalThis`, no DOM,
 * no `constructor` / prototype escapes.
 *
 * Supported: literals (string/number/bool/null), identifiers (scope + a small
 * set of safe globals), member access (`a.b`, `a["b"]`, `a[0]`, `.length`),
 * unary (`! - +`), binary (`+ - * / %`, `=== !== == != < <= > >=`),
 * logical (`&& || ??`, short-circuit), ternary, array/object literals,
 * **arrow functions** (`x => …`, `(a, b) => …`) and **method calls** over a
 * whitelist (Array / String / Number helpers + `Math` / `JSON` / `Object` /
 * `Array` / `Number` / `String` / `Boolean` globals). So
 * `rows.filter(r => r.stage === 'won').reduce((a, r) => a + r.amount, 0)`
 * evaluates. Anything outside the whitelist (arbitrary calls, prototype
 * access) throws.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import { FORMULA_FNS } from "./pageFormulas";

export type Scope = Record<string, unknown>;

type NodeType =
  | 'Literal'
  | 'Identifier'
  | 'Member'
  | 'Unary'
  | 'Binary'
  | 'Logical'
  | 'Conditional'
  | 'Array'
  | 'Object'
  | 'Call'
  | 'Arrow';

export interface Node {
  type: NodeType;
  value?: unknown;
  name?: string;
  object?: Node;
  property?: Node | string;
  computed?: boolean;
  op?: string;
  argument?: Node;
  left?: Node;
  right?: Node;
  test?: Node;
  consequent?: Node;
  alternate?: Node;
  elements?: Node[];
  callee?: Node;
  args?: Node[];
  properties?: { key: string; value: Node }[];
  // Arrow
  params?: string[];
  body?: Node;
}

export class ExprError extends Error {}

/**
 * Marker for a JS Object namespace (ADR-0044 J1). A page's JS Object compiles
 * (in the runtime, real JS — the ADR's "client native-JS host") to a namespace
 * object whose values are the operator-authored functions; it's tagged with
 * this non-enumerable symbol so the interpreter knows a `{{ Utils.fn(a) }}` call
 * is an ALLOWED client-logic call (the receiver is trusted, operator-authored).
 * Without the tag a plain-object method call stays rejected (the safe default).
 */
const JS_OBJECT_TAG = Symbol.for('tray.jsObjectNamespace');

/** Tag `ns` as a callable JS Object namespace and return it (the runtime calls
 *  this once per JS Object after compiling its module). */
export function asJsObjectNamespace<T extends object>(ns: T): T {
  Object.defineProperty(ns, JS_OBJECT_TAG, { value: true, enumerable: false });
  return ns;
}

/** True if `v` is a JS Object namespace tagged by {@link asJsObjectNamespace}.
 *  Exported so the builder intellisense (`pagePreview`) can declare each JS
 *  Object's functions from the same compiled namespaces the runtime uses. */
export function isJsObjectNamespace(v: unknown): boolean {
  return (
    v != null &&
    typeof v === 'object' &&
    (v as Record<symbol, unknown>)[JS_OBJECT_TAG] === true
  );
}

// ---------------------------------------------------------------------------
// Trusted external-library values (ADR-0046)
// ---------------------------------------------------------------------------
//
// A registered external JS library global (lodash `_`, dayjs, …) is loaded by
// the operator (`apps:write`) and is operator-chosen client code of the SAME
// trust class as a JS Object. The ADR keystone: register the global as a
// TRUSTED NAMESPACE on which free member-access + call chaining is allowed —
// transitively through its return values + members, so `_.sumBy(rows, 'x')` and
// `dayjs(x).format('YYYY')` evaluate — while EVERYTHING ELSE stays restricted
// exactly as today. Trust only ever propagates FROM an already-trusted value
// (a library global the loader marked, or something derived from one), so user
// data from `scope` is never trusted.
//
// CRITICAL: the `DENIED_PROPS` guard (__proto__/constructor/prototype) still
// fires on a trusted value, so this is NOT a `new Function` escape — a binding
// like `{{ _.map.constructor(...) }}` still throws. Bindings are operator-
// authored (never end-user input); this widening is the "trusted-namespace
// exception" the ADR locks, not an opening of the restricted AST.
const TRUSTED_LIB = new WeakSet<object>();

/** Tag `v` as a trusted external-library value (the loader calls this once per
 *  library global). Non-objects pass through untouched (primitives are leaves —
 *  a string result already routes through STRING_METHODS). */
export function asTrustedLibValue<T>(v: T): T {
  return markTrusted(v) as T;
}

function markTrusted(v: unknown): unknown {
  if (v != null && (typeof v === 'object' || typeof v === 'function')) {
    TRUSTED_LIB.add(v as object);
  }
  return v;
}

function isTrustedLib(v: unknown): boolean {
  return (
    v != null &&
    (typeof v === 'object' || typeof v === 'function') &&
    TRUSTED_LIB.has(v as object)
  );
}

/** Member names that must never be readable (prototype-pollution / escapes). */
const DENIED_PROPS = new Set(['__proto__', 'constructor', 'prototype']);

const MAX_NODES = 500; // parse-time AST size cap (cheap DoS guard)
const MAX_DEPTH = 64; // eval recursion cap
const RESERVED = new Set(['true', 'false', 'null', 'undefined']);

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type Tok =
  | { t: 'num'; v: number }
  | { t: 'str'; v: string }
  | { t: 'ident'; v: string }
  | { t: 'punc'; v: string }
  | { t: 'eof' };

const PUNCT = [
  '===', '!==', '==', '!=', '<=', '>=', '&&', '||', '??', '=>', '?.',
  '(', ')', '[', ']', '{', '}', ',', ':', '?', '.',
  '+', '-', '*', '/', '%', '!', '<', '>',
];

function tokenize(src: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  const n = src.length;
  while (i < n) {
    const c = src[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      i++;
      continue;
    }
    if (c === '"' || c === "'") {
      const quote = c;
      let j = i + 1;
      let out = '';
      while (j < n && src[j] !== quote) {
        if (src[j] === '\\' && j + 1 < n) {
          const e = src[j + 1];
          out += e === 'n' ? '\n' : e === 't' ? '\t' : e;
          j += 2;
        } else {
          out += src[j];
          j++;
        }
      }
      if (j >= n) throw new ExprError('unterminated string');
      toks.push({ t: 'str', v: out });
      i = j + 1;
      continue;
    }
    if (c >= '0' && c <= '9') {
      let j = i;
      while (j < n && ((src[j] >= '0' && src[j] <= '9') || src[j] === '.')) j++;
      toks.push({ t: 'num', v: Number(src.slice(i, j)) });
      i = j;
      continue;
    }
    if (/[A-Za-z_$]/.test(c)) {
      let j = i;
      while (j < n && /[A-Za-z0-9_$]/.test(src[j])) j++;
      toks.push({ t: 'ident', v: src.slice(i, j) });
      i = j;
      continue;
    }
    const three = src.slice(i, i + 3);
    const two = src.slice(i, i + 2);
    if (PUNCT.includes(three)) {
      toks.push({ t: 'punc', v: three });
      i += 3;
      continue;
    }
    if (PUNCT.includes(two)) {
      toks.push({ t: 'punc', v: two });
      i += 2;
      continue;
    }
    if (PUNCT.includes(c)) {
      toks.push({ t: 'punc', v: c });
      i += 1;
      continue;
    }
    throw new ExprError(`unexpected character '${c}'`);
  }
  toks.push({ t: 'eof' });
  return toks;
}

// ---------------------------------------------------------------------------
// Parser (Pratt / precedence-climbing)
// ---------------------------------------------------------------------------

const BIN_PREC: Record<string, number> = {
  '||': 1, '??': 1, '&&': 2,
  '==': 3, '!=': 3, '===': 3, '!==': 3,
  '<': 4, '>': 4, '<=': 4, '>=': 4,
  '+': 5, '-': 5, '*': 6, '/': 6, '%': 6,
};
const LOGICAL = new Set(['||', '&&', '??']);

class Parser {
  private pos = 0;
  private count = 0;
  constructor(private readonly toks: Tok[]) {}

  private peek(): Tok {
    return this.toks[this.pos];
  }
  private next(): Tok {
    return this.toks[this.pos++];
  }
  private isPunc(v: string): boolean {
    const tk = this.peek();
    return tk.t === 'punc' && tk.v === v;
  }
  private node<T extends Node>(node: T): T {
    if (++this.count > MAX_NODES) throw new ExprError('expression too large');
    return node;
  }
  private expectPunc(v: string): void {
    const tk = this.next();
    if (tk.t !== 'punc' || tk.v !== v) throw new ExprError(`expected '${v}'`);
  }

  parse(): Node {
    const node = this.parseAssign();
    if (this.peek().t !== 'eof') throw new ExprError('unexpected trailing input');
    return node;
  }

  /** Entry for any sub-expression: try an arrow first, else a binary expr. */
  private parseAssign(): Node {
    const start = this.pos;
    const arrow = this.tryArrow();
    if (arrow) return arrow;
    this.pos = start;
    return this.parseExpr(0);
  }

  /** `ident => body` or `(a, b) => body`; returns null (no consume guarantee)
   *  if the lookahead isn't an arrow — caller restores `pos`. */
  private tryArrow(): Node | null {
    let params: string[] | null = null;
    const t0 = this.peek();
    if (t0.t === 'ident' && !RESERVED.has(t0.v)) {
      this.next();
      if (this.isPunc('=>')) params = [t0.v];
      else return null;
    } else if (t0.t === 'punc' && t0.v === '(') {
      this.next();
      const ps: string[] = [];
      if (!this.isPunc(')')) {
        for (;;) {
          const p = this.next();
          if (p.t !== 'ident') return null;
          ps.push(p.v);
          if (this.isPunc(',')) {
            this.next();
            continue;
          }
          break;
        }
      }
      if (!this.isPunc(')')) return null;
      this.next();
      if (!this.isPunc('=>')) return null;
      params = ps;
    }
    if (params === null) return null;
    this.next(); // consume '=>'
    const body = this.parseAssign();
    return this.node<Node>({ type: 'Arrow', params, body });
  }

  private parseExpr(minPrec: number): Node {
    let left = this.parseUnary();
    for (;;) {
      const tk = this.peek();
      if (tk.t === 'punc' && tk.v in BIN_PREC) {
        const prec = BIN_PREC[tk.v];
        if (prec < minPrec) break;
        this.next();
        const right = this.parseExpr(prec + 1);
        left = this.node<Node>({
          type: LOGICAL.has(tk.v) ? 'Logical' : 'Binary',
          op: tk.v,
          left,
          right,
        });
      } else {
        break;
      }
    }
    if (minPrec === 0 && this.isPunc('?')) {
      this.next();
      const consequent = this.parseAssign();
      this.expectPunc(':');
      const alternate = this.parseAssign();
      return this.node<Node>({ type: 'Conditional', test: left, consequent, alternate });
    }
    return left;
  }

  private parseUnary(): Node {
    const tk = this.peek();
    if (tk.t === 'punc' && (tk.v === '!' || tk.v === '-' || tk.v === '+')) {
      this.next();
      return this.node<Node>({ type: 'Unary', op: tk.v, argument: this.parseUnary() });
    }
    return this.parsePostfix();
  }

  private parsePostfix(): Node {
    let node = this.parsePrimary();
    for (;;) {
      const tk = this.peek();
      if (tk.t === 'punc' && tk.v === '.') {
        this.next();
        const id = this.next();
        if (id.t !== 'ident') throw new ExprError('expected property name');
        node = this.node<Node>({ type: 'Member', object: node, property: id.v, computed: false });
      } else if (tk.t === 'punc' && tk.v === '?.') {
        // Optional chaining `a?.b` / `a?.[x]` / `a?.(args)`. Member access is
        // already null-safe (readMember returns undefined on a null/undefined
        // object), so `?.` mainly lets the natural defensive syntax COMPILE —
        // a binding written with `?.` would otherwise be a parse error and the
        // whole template would silently fail (the KPI '0' bug).
        this.next();
        const nxt = this.peek();
        if (nxt.t === 'punc' && nxt.v === '[') {
          this.next();
          const prop = this.parseExpr(0);
          this.expectPunc(']');
          node = this.node<Node>({ type: 'Member', object: node, property: prop, computed: true });
        } else if (nxt.t === 'punc' && nxt.v === '(') {
          this.next();
          const args: Node[] = [];
          if (!this.isPunc(')')) {
            for (;;) {
              args.push(this.parseAssign());
              if (this.isPunc(',')) { this.next(); continue; }
              break;
            }
          }
          this.expectPunc(')');
          node = this.node<Node>({ type: 'Call', callee: node, args });
        } else {
          const id = this.next();
          if (id.t !== 'ident') throw new ExprError('expected property name');
          node = this.node<Node>({ type: 'Member', object: node, property: id.v, computed: false });
        }
      } else if (tk.t === 'punc' && tk.v === '[') {
        this.next();
        const prop = this.parseExpr(0);
        this.expectPunc(']');
        node = this.node<Node>({ type: 'Member', object: node, property: prop, computed: true });
      } else if (tk.t === 'punc' && tk.v === '(') {
        this.next();
        const args: Node[] = [];
        if (!this.isPunc(')')) {
          for (;;) {
            args.push(this.parseAssign());
            if (this.isPunc(',')) {
              this.next();
              continue;
            }
            break;
          }
        }
        this.expectPunc(')');
        node = this.node<Node>({ type: 'Call', callee: node, args });
      } else {
        break;
      }
    }
    return node;
  }

  private parsePrimary(): Node {
    const tk = this.next();
    if (tk.t === 'num') return this.node<Node>({ type: 'Literal', value: tk.v });
    if (tk.t === 'str') return this.node<Node>({ type: 'Literal', value: tk.v });
    if (tk.t === 'ident') {
      if (tk.v === 'true') return this.node<Node>({ type: 'Literal', value: true });
      if (tk.v === 'false') return this.node<Node>({ type: 'Literal', value: false });
      if (tk.v === 'null') return this.node<Node>({ type: 'Literal', value: null });
      if (tk.v === 'undefined') return this.node<Node>({ type: 'Literal', value: undefined });
      return this.node<Node>({ type: 'Identifier', name: tk.v });
    }
    if (tk.t === 'punc' && tk.v === '(') {
      const inner = this.parseAssign();
      this.expectPunc(')');
      return inner;
    }
    if (tk.t === 'punc' && tk.v === '[') {
      const elements: Node[] = [];
      if (!this.isPunc(']')) {
        for (;;) {
          elements.push(this.parseAssign());
          if (this.isPunc(',')) {
            this.next();
            continue;
          }
          break;
        }
      }
      this.expectPunc(']');
      return this.node<Node>({ type: 'Array', elements });
    }
    if (tk.t === 'punc' && tk.v === '{') {
      const properties: { key: string; value: Node }[] = [];
      if (!this.isPunc('}')) {
        for (;;) {
          const k = this.next();
          let key: string;
          if (k.t === 'ident') key = k.v;
          else if (k.t === 'str') key = k.v;
          else throw new ExprError('expected object key');
          this.expectPunc(':');
          properties.push({ key, value: this.parseAssign() });
          if (this.isPunc(',')) {
            this.next();
            continue;
          }
          break;
        }
      }
      this.expectPunc('}');
      return this.node<Node>({ type: 'Object', properties });
    }
    throw new ExprError('unexpected token');
  }
}

/** Parse a raw expression (no `{{ }}`) into an AST. Throws on syntax error. */
export function parseExpr(src: string): Node {
  return new Parser(tokenize(src)).parse();
}

// ---------------------------------------------------------------------------
// Safe globals + method whitelist
// ---------------------------------------------------------------------------

// Namespaces reachable as bare identifiers — all PURE, no side effects, no way
// back to `window` (their methods are whitelisted per-receiver below).
const SAFE_OBJECT = { keys: Object.keys, values: Object.values, entries: Object.entries };
const SAFE_ARRAY = { isArray: Array.isArray };
const SAFE_JSON = { stringify: JSON.stringify, parse: JSON.parse };
const GLOBALS: Record<string, unknown> = {
  Math,
  JSON: SAFE_JSON,
  Object: SAFE_OBJECT,
  Array: SAFE_ARRAY,
  Number,
  String,
  Boolean,
  parseInt,
  parseFloat,
  isNaN,
  isFinite,
  // Spreadsheet-style formula functions (SUM, IF, CONCAT, FORMATDATE, …) —
  // familiar to non-developers. Pure host functions, so they don't widen the
  // sandbox escape surface (only this fixed set is callable). See pageFormulas.
  ...FORMULA_FNS,
};

/**
 * Bare identifiers that resolve to a safe GLOBAL, NOT to the caller scope.
 * Derived from `GLOBALS` so it can never drift from the evaluator. The
 * reactive dependency extractor (`lib/reactive/deps.ts`, ADR-0043 F1) uses
 * this to skip globals when collecting which scope paths a binding reads —
 * a `{{ Math.round(x) }}` depends on `x`, never on "Math".
 */
export const GLOBAL_NAMES: ReadonlySet<string> = new Set(Object.keys(GLOBALS));
// Bare-identifier functions that may be CALLED directly: coercion/parse globals
// + the formula functions.
const CALLABLE_GLOBALS = new Set<unknown>([
  Number,
  String,
  Boolean,
  parseInt,
  parseFloat,
  isNaN,
  isFinite,
  ...Object.values(FORMULA_FNS),
]);

const ARRAY_METHODS = new Set([
  'filter', 'map', 'reduce', 'reduceRight', 'find', 'findIndex', 'some', 'every',
  'slice', 'includes', 'indexOf', 'lastIndexOf', 'join', 'concat', 'flat', 'flatMap', 'at',
]);
const ARRAY_COPY_METHODS = new Set(['sort', 'reverse']); // mutating → run on a copy
const STRING_METHODS = new Set([
  'toUpperCase', 'toLowerCase', 'trim', 'trimStart', 'trimEnd', 'includes', 'startsWith',
  'endsWith', 'slice', 'substring', 'substr', 'split', 'replace', 'replaceAll', 'padStart',
  'padEnd', 'repeat', 'charAt', 'charCodeAt', 'indexOf', 'lastIndexOf', 'at', 'concat',
]);
const NUMBER_METHODS = new Set(['toFixed', 'toPrecision', 'toString']);
const MATH_METHODS = new Set([
  'abs', 'ceil', 'floor', 'round', 'trunc', 'sign', 'sqrt', 'cbrt', 'pow', 'exp', 'log',
  'log2', 'log10', 'min', 'max', 'hypot',
]);

// ---------------------------------------------------------------------------
// Evaluator
// ---------------------------------------------------------------------------

const hasOwn = Object.prototype.hasOwnProperty;

function readMember(obj: unknown, prop: string): unknown {
  if (DENIED_PROPS.has(prop)) throw new ExprError(`access to '${prop}' is denied`);
  // Null-safe: reading a property off null/undefined yields undefined (not a
  // throw), so a chain like `$queries.x.rows[0].count` renders empty when the
  // data isn't there yet instead of failing the WHOLE binding — and `a?.b.c`
  // short-circuits naturally. The DENIED_PROPS guard above is unaffected (still
  // the security boundary). Lenient template semantics, not a wider surface.
  if (obj == null) return undefined;
  // ADR-0046 — a trusted external-library value: reach its (prototype) members
  // too — a Dayjs instance's `.format`, `_.fp`, etc. live on the prototype, so
  // the `hasOwn` restriction below would hide them. The result stays trusted so
  // chaining continues. DENIED_PROPS already fired above, so `.constructor` etc.
  // still throw — this is reach into operator-trusted library code, never an escape.
  if (isTrustedLib(obj)) return markTrusted((obj as Record<string, unknown>)[prop]);
  if (typeof obj === 'string' || Array.isArray(obj)) {
    if (prop === 'length') return (obj as { length: number }).length;
    if (Array.isArray(obj)) {
      const idx = Number(prop);
      if (Number.isInteger(idx) && idx >= 0) return obj[idx];
    }
  }
  if (typeof obj === 'object' && hasOwn.call(obj, prop)) {
    return (obj as Record<string, unknown>)[prop];
  }
  return undefined; // unknown / inherited members are never reachable
}

/** Apply a prototype method `recv[method](...args)` with `recv` as `this`. */
function invoke(recv: unknown, method: string, args: unknown[]): unknown {
  const fn = (recv as Record<string, unknown>)[method];
  if (typeof fn !== 'function') throw new ExprError(`method '${method}' is not callable`);
  return (fn as (...a: unknown[]) => unknown).apply(recv, args);
}

/** Resolve a method call on a value against the per-type whitelist. */
function callMethod(recv: unknown, method: string, args: unknown[]): unknown {
  if (DENIED_PROPS.has(method)) throw new ExprError(`method '${method}' is denied`);
  // JS Object call (ADR-0044): `Utils.fn(args)` where `Utils` is a tagged
  // namespace of operator-authored functions. The args were evaluated by the
  // safe interpreter; the function itself is the trusted client logic.
  if (isJsObjectNamespace(recv)) {
    const fn = (recv as Record<string, unknown>)[method];
    // `apply(recv, …)` so a function can call a sibling via `this.other()`.
    if (typeof fn === 'function') return (fn as (...a: unknown[]) => unknown).apply(recv, args);
    throw new ExprError(`JS Object has no function '${method}'`);
  }
  // ADR-0046 — a trusted external-library value: any method may be called
  // (operator-chosen library code), and the result stays trusted so a chain like
  // `dayjs(x).format(...)` / `_(rows).map(...).value()` keeps working. A DENIED
  // method name threw at the top, so this never reaches `constructor`/`prototype`.
  if (isTrustedLib(recv)) {
    const fn = (recv as Record<string, unknown>)[method];
    if (typeof fn === 'function') {
      return markTrusted((fn as (...a: unknown[]) => unknown).apply(recv, args));
    }
    throw new ExprError(`'${method}' is not a function on this library value`);
  }
  if (Array.isArray(recv)) {
    if (ARRAY_METHODS.has(method)) return invoke(recv, method, args);
    if (ARRAY_COPY_METHODS.has(method)) return invoke(recv.slice(), method, args);
  } else if (typeof recv === 'string') {
    if (STRING_METHODS.has(method)) return invoke(recv, method, args);
  } else if (typeof recv === 'number') {
    if (NUMBER_METHODS.has(method)) return invoke(recv, method, args);
  } else if (recv === Math) {
    if (MATH_METHODS.has(method)) return invoke(Math, method, args);
  } else if (recv === SAFE_OBJECT || recv === SAFE_ARRAY || recv === SAFE_JSON) {
    const fn = (recv as Record<string, unknown>)[method];
    if (typeof fn === 'function') return (fn as (...a: unknown[]) => unknown)(...args);
  }
  throw new ExprError(`method '${method}' is not allowed on this value`);
}

function evalNode(node: Node, scope: Scope, depth: number): unknown {
  if (depth > MAX_DEPTH) throw new ExprError('expression too deep');
  switch (node.type) {
    case 'Literal':
      return node.value;
    case 'Identifier': {
      const name = node.name!;
      if (hasOwn.call(scope, name)) return scope[name];
      if (hasOwn.call(GLOBALS, name)) return GLOBALS[name];
      return undefined;
    }
    case 'Member': {
      const obj = evalNode(node.object!, scope, depth + 1);
      const prop = node.computed
        ? String(evalNode(node.property as Node, scope, depth + 1))
        : (node.property as string);
      return readMember(obj, prop);
    }
    case 'Unary': {
      const v = evalNode(node.argument!, scope, depth + 1);
      if (node.op === '!') return !v;
      if (node.op === '-') return -(v as number);
      return +(v as number);
    }
    case 'Logical': {
      const l = evalNode(node.left!, scope, depth + 1);
      if (node.op === '&&') return l ? evalNode(node.right!, scope, depth + 1) : l;
      if (node.op === '||') return l ? l : evalNode(node.right!, scope, depth + 1);
      return l == null ? evalNode(node.right!, scope, depth + 1) : l;
    }
    case 'Binary': {
      const l = evalNode(node.left!, scope, depth + 1) as never;
      const r = evalNode(node.right!, scope, depth + 1) as never;
      switch (node.op) {
        case '+': return (l as number) + (r as number);
        case '-': return (l as number) - (r as number);
        case '*': return (l as number) * (r as number);
        case '/': return (l as number) / (r as number);
        case '%': return (l as number) % (r as number);
        case '===': return l === r;
        case '!==': return l !== r;
        case '==': return l == r;
        case '!=': return l != r;
        case '<': return l < r;
        case '>': return l > r;
        case '<=': return l <= r;
        case '>=': return l >= r;
        default: throw new ExprError(`unknown operator '${node.op}'`);
      }
    }
    case 'Conditional':
      return evalNode(node.test!, scope, depth + 1)
        ? evalNode(node.consequent!, scope, depth + 1)
        : evalNode(node.alternate!, scope, depth + 1);
    case 'Array':
      return node.elements!.map((e) => evalNode(e, scope, depth + 1));
    case 'Object': {
      const out: Record<string, unknown> = {};
      for (const { key, value } of node.properties!) {
        if (DENIED_PROPS.has(key)) throw new ExprError(`key '${key}' is denied`);
        out[key] = evalNode(value, scope, depth + 1);
      }
      return out;
    }
    case 'Arrow': {
      // A closure over `scope`; params shadow outer names. Only ever invoked by
      // our own whitelisted methods (filter/reduce/…), never by host code.
      const params = node.params!;
      const body = node.body!;
      return (...args: unknown[]) => {
        const child: Scope = { ...scope };
        params.forEach((p, i) => {
          child[p] = args[i];
        });
        return evalNode(body, child, depth + 1);
      };
    }
    case 'Call': {
      const callee = node.callee!;
      const args = node.args!.map((a) => evalNode(a, scope, depth + 1));
      if (callee.type === 'Member') {
        const recv = evalNode(callee.object!, scope, depth + 1);
        // Null-safe: a method call on null/undefined short-circuits to undefined
        // (so `rows.find(...)?.x` and `a?.b()` don't blow up the whole binding).
        if (recv == null) return undefined;
        const method = callee.computed
          ? String(evalNode(callee.property as Node, scope, depth + 1))
          : (callee.property as string);
        return callMethod(recv, method, args);
      }
      // Bare call: only the safe coercion/parse globals are callable…
      const fn = evalNode(callee, scope, depth + 1);
      // …plus a trusted external-library that is itself callable (ADR-0046,
      // e.g. `dayjs(x)` / `_(rows)`); the result stays trusted so it chains.
      if (isTrustedLib(fn) && typeof fn === 'function') {
        return markTrusted((fn as (...a: unknown[]) => unknown)(...args));
      }
      if (typeof fn === 'function' && CALLABLE_GLOBALS.has(fn)) {
        return (fn as (...a: unknown[]) => unknown)(...args);
      }
      throw new ExprError('function calls are not allowed here');
    }
    default:
      throw new ExprError(`unknown node '${node.type}'`);
  }
}

/** Parse + evaluate a raw expression against `scope`. Throws on syntax or
 *  unsafe-access errors. */
export function evalExpr(src: string, scope: Scope = {}): unknown {
  return evalNode(parseExpr(src), scope, 0);
}

// ---------------------------------------------------------------------------
// Template interpolation
// ---------------------------------------------------------------------------

interface Segment {
  text: string;
  isExpr: boolean;
}

/** Split a template into literal-text / `{{ expr }}` segments. Brace- and
 *  string-aware so object literals inside `{{ }}` don't mis-close. */
function splitTemplate(tpl: string): Segment[] {
  const segs: Segment[] = [];
  let i = 0;
  const n = tpl.length;
  let text = '';
  while (i < n) {
    if (tpl[i] === '{' && tpl[i + 1] === '{') {
      if (text) {
        segs.push({ text, isExpr: false });
        text = '';
      }
      i += 2;
      let depth = 0;
      let expr = '';
      let quote = '';
      while (i < n) {
        const c = tpl[i];
        if (quote) {
          expr += c;
          if (c === '\\' && i + 1 < n) {
            expr += tpl[i + 1];
            i += 2;
            continue;
          }
          if (c === quote) quote = '';
          i++;
          continue;
        }
        if (c === '"' || c === "'") {
          quote = c;
          expr += c;
          i++;
          continue;
        }
        if (c === '{') depth++;
        if (c === '}') {
          if (depth === 0 && tpl[i + 1] === '}') {
            i += 2;
            break;
          }
          depth--;
        }
        expr += c;
        i++;
      }
      segs.push({ text: expr.trim(), isExpr: true });
    } else {
      text += tpl[i];
      i++;
    }
  }
  if (text) segs.push({ text, isExpr: false });
  return segs;
}

/**
 * The expression sources of a template's `{{ … }}` segments, in order (literal
 * text dropped). `"Hi {{ a }} and {{ b }}"` → `["a", "b"]`; a brace-free string
 * → `[]`. Reuses the SAME brace/quote-aware splitter as `renderTemplate`, so
 * the reactive dependency extractor (`lib/reactive/deps.ts`) can find a
 * template's reads without drifting from how it's actually rendered.
 */
export function templateExpressions(tpl: string): string[] {
  if (typeof tpl !== 'string' || !tpl.includes('{{')) return [];
  return splitTemplate(tpl)
    .filter((s) => s.isExpr)
    .map((s) => s.text);
}

/**
 * Render a template: interpolate every `{{ expr }}` against `scope`.
 *
 * A template that is a SINGLE `{{ expr }}` with no surrounding non-empty
 * text returns the **raw typed value** (number/bool/object preserved) —
 * mirroring the backend `render_template`. Anything mixed → string concat
 * (`null`/`undefined` render as empty). Literal text is free-form.
 */
export function renderTemplate(tpl: string, scope: Scope = {}): unknown {
  if (typeof tpl !== 'string' || !tpl.includes('{{')) return tpl;
  const segs = splitTemplate(tpl);
  const meaningful = segs.filter((s) => s.isExpr || s.text.length > 0);
  if (meaningful.length === 1 && meaningful[0].isExpr) {
    return evalExpr(meaningful[0].text, scope);
  }
  let out = '';
  for (const s of segs) {
    if (s.isExpr) {
      const v = evalExpr(s.text, scope);
      out += v == null ? '' : String(v);
    } else {
      out += s.text;
    }
  }
  return out;
}
